import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "../api/axios";

const AuthContext = createContext({});

// Helper function to get tenant_id from URL params (fallback for direct access)
const getTenantIdFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("tenant_id") || params.get("tenantId");
};

// Helper function to extract tenant_id from consent-related URL params
const getTenantIdFromConsentUrl = () => {
  const params = new URLSearchParams(window.location.search);
  // The backend should include tenantId alongside consentId in the redirect
  return params.get("tenantId") || params.get("tenant_id");
};

export const AuthProvider = ({ children }) => {
  // Multi-tenant auth sessions: array of { tenant_id, auth, permissions, role, created_at, updated_at }
  const [tenantSessions, setTenantSessions] = useState(() => {
    // Load from localStorage on initialization
    try {
      const stored = localStorage.getItem("tenantSessions");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Legacy single auth for backward compatibility (will be derived from current tenant session)
  const [auth, setAuth] = useState({});
  const [persist, setPersist] = useState(
    JSON.parse(localStorage.getItem("persist")) || false
  );
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState([]);

  const [redirectUri, setRedirectUri] = useState();
  const [currentTenant, setCurrentTenant] = useState(null);
  const [userTenants, setUserTenants] = useState([]);
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);

  // Helper functions for managing multi-tenant sessions
  const addOrUpdateTenantSession = (
    tenantId,
    authData,
    permissionsData = [],
    roleData = []
  ) => {
    const now = new Date().toISOString();
    const tokenIssuedAt = new Date().getTime(); // Unix timestamp for precise calculations

    setTenantSessions((prev) => {
      const existingIndex = prev.findIndex(
        (session) => session.tenant_id === tenantId
      );
      const sessionData = {
        tenant_id: tenantId,
        auth: {
          ...authData,
          // Add token issue timestamp for expiration validation
          token_issued_at: tokenIssuedAt,
        },
        permissions: permissionsData,
        role: roleData,
        updated_at: now,
        created_at: existingIndex >= 0 ? prev[existingIndex].created_at : now,
      };

      let newSessions;
      if (existingIndex >= 0) {
        // Update existing session
        newSessions = [...prev];
        newSessions[existingIndex] = sessionData;
      } else {
        // Add new session
        newSessions = [...prev, sessionData];
      }

      // Persist to localStorage
      try {
        localStorage.setItem("tenantSessions", JSON.stringify(newSessions));
      } catch (error) {
        console.warn("Failed to save tenant sessions to localStorage:", error);
      }

      return newSessions;
    });
  };

  const getTenantSession = (tenantId) => {
    return (
      tenantSessions.find((session) => session.tenant_id === tenantId) || null
    );
  };

  const removeTenantSession = (tenantId) => {
    setTenantSessions((prev) => {
      const newSessions = prev.filter(
        (session) => session.tenant_id !== tenantId
      );
      try {
        localStorage.setItem("tenantSessions", JSON.stringify(newSessions));
      } catch (error) {
        console.warn(
          "Failed to update tenant sessions in localStorage:",
          error
        );
      }
      return newSessions;
    });
  };

  const clearAllTenantSessions = () => {
    setTenantSessions([]);
    try {
      localStorage.removeItem("tenantSessions");
    } catch (error) {
      console.warn("Failed to clear tenant sessions from localStorage:", error);
    }
  };

  // Clear tenant_id from the URL without reloading
  const clearTenantFromUrl = () => {
    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("tenant_id");
      currentUrl.searchParams.delete("tenantId");
      window.history.replaceState({}, "", currentUrl.toString());
    } catch (e) {
      console.warn("Failed to clear tenant params from URL", e);
    }
  };

  // Clear current tenant context (and optionally URL params)
  const clearTenantContext = (clearUrlParams = true) => {
    setCurrentTenant(null);
    if (clearUrlParams) clearTenantFromUrl();
  };

  // Helper function to check if auth token is valid/not expired
  const isTokenValid = (authData) => {
    if (!authData || !authData.access_token) {
      return false;
    }

    // Check if token has expired based on issue time and expires_in
    if (authData.expires_in && authData.token_issued_at) {
      const now = new Date().getTime();
      const expirationTime =
        authData.token_issued_at + authData.expires_in * 1000; // Convert to milliseconds

      // Add a 30-second buffer to avoid edge cases
      const bufferTime = 30 * 1000;

      if (now >= expirationTime - bufferTime) {
        console.log(
          `Token expired for tenant. Issued at: ${new Date(
            authData.token_issued_at
          )}, Expires in: ${authData.expires_in}s`
        );
        return false;
      }

      return true;
    }

    // If no expiration info but has token, assume valid (backend will validate)
    // This handles cases where the token doesn't include expiration info
    return authData.access_token ? true : false;
  };

  const switchToTenant = useMemo(
    () =>
      async (tenantId, redirectToLogin = true) => {
        const session = getTenantSession(tenantId);

        if (session && isTokenValid(session.auth)) {
          // Valid session exists, switch to it
          setCurrentTenant({ id: tenantId });
          setAuth(session.auth || {});
          setPermissions(session.permissions || []);
          setRole(session.role || []);

          // Update URL to reflect tenant context
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set("tenant_id", tenantId);
          window.history.replaceState({}, "", currentUrl.toString());

          return { success: true, authenticated: true };
        } else {
          // No valid session for this tenant
          if (session) {
            // Remove invalid/expired session
            removeTenantSession(tenantId);
          }

          if (redirectToLogin) {
            // Set the tenant context and redirect to login
            setCurrentTenant({ id: tenantId });
            setAuth({});
            setPermissions([]);
            setRole([]);

            // Redirect to login with tenant context
            const loginUrl = `/login?tenant_id=${tenantId}`;
            window.location.href = loginUrl;

            return { success: true, authenticated: false, redirected: true };
          } else {
            // Just set tenant context without redirect (for programmatic use)
            setCurrentTenant({ id: tenantId });
            setAuth({});
            setPermissions([]);
            setRole([]);

            return { success: true, authenticated: false, redirected: false };
          }
        }
      },
    [getTenantSession]
  );

  // Enhanced setAuth that handles multi-tenant sessions
  const setAuthWithTenant = useCallback(
    (authData, tenantId = null) => {
      const targetTenantId =
        tenantId || currentTenant?.id || getTenantIdFromUrl();

      if (targetTenantId) {
        // Update the specific tenant session
        addOrUpdateTenantSession(targetTenantId, authData, permissions, role);

        // If this is the current tenant, update legacy state for backward compatibility
        if (targetTenantId === currentTenant?.id) {
          setAuth(authData);
        }
      } else {
        // Fallback to legacy behavior if no tenant context
        setAuth(authData);
      }
    },
    [addOrUpdateTenantSession, currentTenant?.id, permissions, role]
  );

  // Helper function to check if user is accessing SSO directly (without tenant context)
  const isDirectSSOAccess = () => {
    const tenantId = getTenantIdFromUrl();
    return !tenantId; // No tenant_id in URL means direct access
  };

  // Helper function to determine if we need tenant selection
  const needsTenantSelection = () => {
    return isDirectSSOAccess() && !currentTenant?.id;
  };

  // Effect to sync current tenant from URL parameters and load session
  useEffect(() => {
    const tenantId = getTenantIdFromUrl();
    if (tenantId && tenantId !== currentTenant?.id) {
      setCurrentTenant({ id: tenantId });

      // Check if we have an existing session for this tenant
      const existingSession = getTenantSession(tenantId);
      if (existingSession && isTokenValid(existingSession.auth)) {
        // Load existing session
        setAuth(existingSession.auth);
        setPermissions(existingSession.permissions || []);
        setRole(existingSession.role || []);
      }
    } else if (!tenantId && currentTenant?.id) {
      // URL doesn't have tenant_id but we have a current tenant
      // This handles the direct SSO access scenario
      // Keep the current tenant context for now
      console.log(
        "Direct SSO access detected - maintaining current tenant context"
      );
    }
  }, [currentTenant?.id, getTenantSession, tenantSessions]);

  useEffect(() => {
    const getAccessToken = async () => {
      try {
        const currentTenant = tenantSessions[tenantSessions.length - 1];
        const tenantId = currentTenant?.tenant_id || getTenantIdFromUrl();

        if (!tenantId) {
          setIsAuthInitializing(false);
          return;
        }

        const response = await axios.get(
          `/refreshToken?tenant_id=${tenantId}`,
          {
            withCredentials: true,
          }
        );

        const authData = response?.data?.data;

        if (tenantId) {
          // Store in tenant-specific session
          addOrUpdateTenantSession(tenantId, authData);
        }

        setAuth(authData);
      } catch (error) {
        setAuth({});
        // If refresh fails for current tenant, remove the invalid session
        const tenantId = getTenantIdFromUrl();
        if (tenantId) {
          removeTenantSession(tenantId);
        }
      } finally {
        setIsAuthInitializing(false);
      }
    };
    getAccessToken();
  }, []);

  const value = useMemo(
    () => ({
      // Legacy single-tenant auth (for backward compatibility)
      auth,
      setAuth,
      persist,
      setPersist,
      permissions,
      setPermissions,
      role,
      setRole,

      // Multi-tenant session management
      tenantSessions,
      setAuthWithTenant,
      addOrUpdateTenantSession,
      getTenantSession,
      removeTenantSession,
      clearAllTenantSessions,
      switchToTenant,

      // Navigation and tenant management
      redirectUri,
      setRedirectUri,
      currentTenant,
      setCurrentTenant,
      userTenants,
      setUserTenants,
      getTenantIdFromUrl,
      getTenantIdFromConsentUrl,

      // Direct SSO access helpers
      isDirectSSOAccess,
      needsTenantSelection,

      // Tenant helpers
      clearTenantContext,

      // Initialization/loading flags
      isAuthInitializing,
    }),
    [
      auth,
      persist,
      permissions,
      role,
      tenantSessions,
      setAuthWithTenant,
      getTenantSession,
      switchToTenant,
      redirectUri,
      currentTenant,
      userTenants,
      needsTenantSelection,
    ]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
